import React, { useMemo, useRef, useState } from 'react';
import { ChevronDown, Lock, Search } from 'lucide-react';
import { ResourceType } from '@cotera/api';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import { cn } from '@cotera/client/v0/lib/utils';
import { RESOURCE_TYPE_CONFIG } from '../type/resource-type-config';
import { ResourceTypeIconBubble } from '../type/resource-type-icon-bubble';
import { shareTargetKey, type ShareTarget } from './share-targets';
import { useShareTargetTitles } from './use-share-access';

/** Above this many, a filter field appears over the list. */
const FILTER_ABOVE = 8;

/**
 * List order: what a chat most often makes first. Sorting by type keeps like
 * items together, so the list reads in runs without needing headers.
 */
const TYPE_ORDER: readonly ResourceType[] = [
  ResourceType.Agent,
  ResourceType.Dataset,
  ResourceType.Document,
  ResourceType.Workflow,
  ResourceType.WorkflowTrigger,
  ResourceType.Folder,
  ResourceType.Chat,
];

/**
 * What sharing a chat hands over: the conversation and everything made in it,
 * all included to start. One row at the top of the share dialog says what
 * that is ("Chat and all 3 artifacts"); it opens a checklist to leave some
 * out. An unticked item stays in the list, marked as locked for them, so what
 * is held back is as visible as what is shared.
 */
export const ShareWhatTheyGet: React.FC<{
  chat: ShareTarget;
  items: ShareTarget[];
  selectedKeys: Set<string>;
  onChange: (next: Set<string>) => void;
}> = ({ chat, items, selectedKeys, onChange }) => {
  const allTargets = useMemo(() => [chat, ...items], [chat, items]);
  const titles = useShareTargetTitles(allTargets);
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  const isOn = (target: ShareTarget) =>
    selectedKeys.has(shareTargetKey(target));
  const setMany = (targets: readonly ShareTarget[], on: boolean) => {
    const next = new Set(selectedKeys);
    for (const target of targets) {
      if (on) {
        next.add(shareTargetKey(target));
      } else {
        next.delete(shareTargetKey(target));
      }
    }
    onChange(next);
  };

  const needle = query.trim().toLowerCase();
  // One list: the conversation first, then what was made in it, by type.
  const ordered = useMemo(
    () => [
      chat,
      ...[...items].sort((a, b) => typeRank(a.type) - typeRank(b.type)),
    ],
    [chat, items]
  );
  const visible =
    needle === ''
      ? ordered
      : ordered.filter((t) =>
          (titles.get(shareTargetKey(t)) ?? '').toLowerCase().includes(needle)
        );

  const itemsOn = items.filter(isOn).length;
  const chatOn = isOn(chat);
  const selectedCount = ordered.filter(isOn).length;
  const everythingOn = selectedCount === ordered.length;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-muted-foreground">Includes</p>
      <Popover
        onOpenChange={(open) => {
          if (!open) {
            setQuery('');
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex min-h-12 w-full items-center gap-3 rounded-lg border border-input bg-transparent px-3 py-2 text-left outline-none transition-[color,box-shadow] hover:bg-muted/40 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 data-[state=open]:border-ring"
          >
            <ResourceTypeIconBubble
              resourceType={ResourceType.Chat}
              icon={RESOURCE_TYPE_CONFIG[ResourceType.Chat].icon}
              className="h-7 w-7"
              iconClassName="h-4 w-4"
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm">
                {summarize({ chatOn, itemsOn, total: items.length })}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {everythingOn
                  ? 'Choose what to leave out'
                  : "Change what's included"}
              </span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          aria-label="Includes"
          // Opens onto the list itself rather than ringing the first button;
          // Tab moves into the checkboxes from there.
          ref={listRef}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            listRef.current?.focus();
          }}
          // Above the modal it opens from, and as wide as the row it opens.
          className="z-50 flex w-(--radix-popover-trigger-width) flex-col overflow-hidden rounded-xl border-border p-0 shadow-lg"
        >
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <p className="flex-1 text-xs text-muted-foreground">
              {selectedCount} of {ordered.length} included
            </p>
            <button
              type="button"
              onClick={() => setMany(ordered, !everythingOn)}
              className="rounded px-1 text-xs font-medium text-primary outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              {everythingOn ? 'Clear' : 'Select all'}
            </button>
          </div>

          {items.length > FILTER_ABOVE ? (
            <label className="mx-2 mt-2 flex h-8 items-center gap-2 rounded-md border border-input bg-background px-2.5 text-muted-foreground focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
              <Search className="h-3.5 w-3.5 shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Filter ${ordered.length} items`}
                aria-label="Filter items"
                className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
              />
            </label>
          ) : null}

          {/* The list scrolls inside the card, so a busy chat stays compact. */}
          <ul className="max-h-72 overflow-y-auto p-1">
            {visible.map((target) => (
              <ItemRow
                key={shareTargetKey(target)}
                target={target}
                title={titles.get(shareTargetKey(target)) ?? ''}
                on={isOn(target)}
                onToggle={(on) => setMany([target], on)}
              />
            ))}
            {needle !== '' && visible.length === 0 ? (
              <li className="px-2 py-2 text-xs text-muted-foreground">
                No items match "{query.trim()}".
              </li>
            ) : null}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const typeRank = (type: ResourceType): number => {
  const rank = TYPE_ORDER.indexOf(type);
  return rank === -1 ? TYPE_ORDER.length : rank;
};

const ItemRow: React.FC<{
  target: ShareTarget;
  title: string;
  on: boolean;
  onToggle: (on: boolean) => void;
}> = ({ target, title, on, onToggle }) => (
  <li>
    <label className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-muted">
      <Checkbox
        checked={on}
        onCheckedChange={(next) => onToggle(next === true)}
        aria-label={title}
      />
      <ResourceTypeIconBubble
        resourceType={target.type}
        icon={RESOURCE_TYPE_CONFIG[target.type].icon}
        className={cn('h-6 w-6', !on && 'opacity-50 grayscale')}
        iconClassName="h-3.5 w-3.5"
      />
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-sm',
          !on && 'text-muted-foreground'
        )}
      >
        {title}
      </span>
      {on ? (
        <span className="shrink-0 text-xs text-muted-foreground">
          {RESOURCE_TYPE_CONFIG[target.type].label}
        </span>
      ) : (
        <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
          <Lock className="h-3 w-3" />
          Locked for them
        </span>
      )}
    </label>
  </li>
);

/** The row's second line: what they get, in one phrase. */
function summarize(params: {
  chatOn: boolean;
  itemsOn: number;
  total: number;
}): string {
  const { chatOn, itemsOn, total } = params;
  const artifacts = (n: number) => `${n} ${n === 1 ? 'artifact' : 'artifacts'}`;
  if (!chatOn && itemsOn === 0) {
    return 'Choose what to share';
  }
  if (!chatOn) {
    return `${artifacts(itemsOn)}, without the chat`;
  }
  if (itemsOn === 0) {
    return 'Chat only, no artifacts';
  }
  if (itemsOn === total) {
    return total === 1
      ? 'Chat and its artifact'
      : `Chat and all ${artifacts(total)}`;
  }
  return `Chat and ${itemsOn} of ${artifacts(total)}`;
}
